import getUserFullName, { parseName } from '@rs-core/fhir/resource/columnMapping/utils/getUserFullName';
import convertArrayToString from '@rs-core/fhir/resource/columnMapping/utils/convertArrayToString';
import getExtensionValueCodingArrayConcat from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueCodingArrayConcat';
import getExtensionValueCoding from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueCoding';
import fhirExtensionUrls from '@rs-core/fhir/extension/fhirExtensionUrls';
import { getDateStringDisplay } from '@rs-ui/components/utils/dateUtils';
import { calculatePatientAge } from '@worklist-2/ui/src/views/PatientInformationView/utils/calculatePatientAge';
import _ from 'lodash';

export function getUniqueOrganizations(arr) {
	const uniqueOrganizations = [];
	const organizationIds = new Set();

	arr.forEach(obj => {
		const orgId = obj?.organization?.id;

		if (!organizationIds.has(orgId)) {
			organizationIds.add(orgId);
			uniqueOrganizations.push(obj?.organization);
		}
	});

	return uniqueOrganizations;
}

/**
 * getTelecomArrayConcat
 * @param {*} telecom
 * @param {*} system
 * @param {*} use
 * @param {*} separator
 * @returns
 */
function getTelecomArrayConcat(telecom, system, use, separator) {
	let valueArray = [];

	const firstLevelExtension = _.filter(telecom, elem => elem.system === system && (use ? elem.use === use : true));

	if (!!firstLevelExtension) {
		valueArray = _.map(firstLevelExtension, item => item.value);
	}

	return valueArray?.join(separator);
}

/**
 * Mapper of order form with pdf template
 * @param {*} formData
 * @param {*} patientCoverageData
 * @param {*} priority
 * @returns
 */
export function getOrderFormDataMapper(formData, patientCoverageData, priority) {
	if (!formData) return null;
	let orderFormDataMapper = [];
	const imagingOrgInfo = formData.getValues('imagingOrganization');
	const patientInfo = formData.getValues('patient');
	const orderSetInfo = formData.getValues('orderSet');
	const notesInfo = formData.getValues('notes');
	const referringPhysicianInfo = formData.getValues('referringPhysician');
	const referringOrgInfo = formData.getValues('referringOrganization');
	const consultingPhysicianInfo = formData.getValues('consultingPhysician');

	// Imaging organization
	orderFormDataMapper['imagingOrganization'] = imagingOrgInfo?.name;

	// Patient
	const patientAddress = patientInfo?.address ? patientInfo?.address[0] : [];
	const patientLanguages =
		patientInfo?.communication && patientInfo?.communication.length > 0 ? patientInfo?.communication[0] : [];
	orderFormDataMapper['state'] = patientAddress?.state;
	orderFormDataMapper['patientAddress'] = patientAddress?.line ? patientAddress.line[0] : '';
	orderFormDataMapper['birthDate'] = getDateStringDisplay(patientInfo?.birthDate);
	orderFormDataMapper['birthSex'] = getExtensionValueCoding(patientInfo, fhirExtensionUrls.common.birthSex);
	orderFormDataMapper['gender'] = patientInfo?.gender?.toUpperCase();
	orderFormDataMapper['patientID'] = patientInfo?.id;
	orderFormDataMapper['patientName'] = patientInfo?.name
		? parseName(patientInfo?.name?.[0]?.text, true, 'DICOM')
		: '';
	orderFormDataMapper['patientAge'] = calculatePatientAge(patientInfo?.birthDate);
	orderFormDataMapper['cellPhone'] = getTelecomArrayConcat(patientInfo?.telecom, 'phone', null, '|');
	orderFormDataMapper['patientEmail'] = getTelecomArrayConcat(patientInfo?.telecom, 'email', null, '|');
	orderFormDataMapper['languages'] = convertArrayToString(patientLanguages.language?.coding);
	orderFormDataMapper['race'] = patientInfo
		? getExtensionValueCodingArrayConcat(patientInfo, fhirExtensionUrls.common.race)
		: '';
	orderFormDataMapper['ethnicity'] = patientInfo
		? getExtensionValueCodingArrayConcat(patientInfo, fhirExtensionUrls.common.ethnicity)
		: '';
	orderFormDataMapper['visitClass'] = patientInfo
		? getExtensionValueCoding(patientInfo, fhirExtensionUrls.patient.encounterClass)?.display
		: '';

	// Patient Insurance
	orderFormDataMapper['insurancePayer'] = patientCoverageData?.payor?.[0]?.display;
	orderFormDataMapper['managingOrganization'] = patientInfo?.managingOrganization?.display;

	// Order set
	const procedurecodes = orderSetInfo?.procedureCode ? orderSetInfo?.procedureCode[0] : [];
	orderFormDataMapper['modality'] = orderSetInfo?.modality?.display;
	orderFormDataMapper['procedureCode'] = convertArrayToString(procedurecodes?.coding, 'code');

	// Order notes
	orderFormDataMapper['orderNotes'] = notesInfo;

	// ReferringPhysician Info
	orderFormDataMapper['referringPhysician'] = referringPhysicianInfo?.name
		? getUserFullName(referringPhysicianInfo?.name[0])
		: '';

	const referringPhysicianNPI = referringPhysicianInfo?.identifier?.find(identifierItem =>
		identifierItem.type?.coding?.some(coding => coding.code === 'NPI')
	);
	orderFormDataMapper['referringPhysicianNPI'] = referringPhysicianNPI?.value;
	orderFormDataMapper['referringPhysicianFaxNumber'] = referringPhysicianInfo?.telecom?.find(
		t => t.system === 'fax'
	)?.value;

	// Referring Organization Info
	orderFormDataMapper['referringFacility'] = referringOrgInfo?.name;

	// Consulting Physician Info
	orderFormDataMapper['consultingPhysician'] = consultingPhysicianInfo
		?.map(physician => getUserFullName(physician.name[0], true))
		.join(' | ');

	//Priority
	orderFormDataMapper['priority'] = priority ? priority.toUpperCase() : '';

	return orderFormDataMapper;
}
